import { useState, useRef, useEffect } from "react";
import {
  Input,
  Row,
  Col,
  List,
  Modal,
  message,
  Button,
  TimePicker,
  Alert,
} from "antd";
import dayjs from "dayjs";
import { EditOutlined, DeleteOutlined, DragOutlined } from "@ant-design/icons";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import ButtonLoader from "../ButtonLoader";

import "../../assets/full-calendar.css";

const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;

// Define schema
const schema = Yup.object().shape({
  name: Yup.string().required("Sequence name is required"),
  events: Yup.array()
    .min(1, "At least one playlist is required")
    .of(
      Yup.object().shape({
        playlistId: Yup.number().required(),
        start: Yup.string()
          .matches(TIME_REGEX, "Start time must be in HH:mm:ss format")
          .required("Start time is required"),
        end: Yup.string()
          .matches(TIME_REGEX, "End time must be in HH:mm:ss format")
          .required("End time is required"),
      })
    ),
});

const SequenceForm = ({
  isSequenceModalOpen,
  closeModal,
  editingSequence,
  handleUpdateList,
  scheduleId,
}) => {
  const [loading, setLoading] = useState(true);
  const [playlists, setPlaylists] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");

  const [isDataSet, setIsDataSet] = useState(false);
  const [highlightedEventId, setHighlightedEventId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [events, setEvents] = useState([]);
  const [selectedDate] = useState(dayjs());
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editEventIndex, setEditEventIndex] = useState(null);
  const [editStart, setEditStart] = useState(null);
  const [editEnd, setEditEnd] = useState(null);
  const calendarRef = useRef(null);

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      events: [],
    },
    context: { isEdit: !!editingSequence },
    resolver: yupResolver(schema),
  });

  const isOverlapping = (newStart, newEnd, ignoreIndex = null) => {
    const checked = events.some((e, i) => {
      if (i === ignoreIndex) return false;

      const existingStart = dayjs(e.start);
      const existingEnd = dayjs(e.end);

      const endsBefore = newEnd.subtract(1, "second").isBefore(existingStart);
      const startsAfter = newStart.add(1, "second").isAfter(existingEnd);

      return !(endsBefore || startsAfter);
    });

    return checked;
  };

  const updateFormEvents = (updatedEvents) => {
    setEvents(updatedEvents);
    setValue(
      "events",
      updatedEvents.map((e) => ({
        playlistId: e.playlistId,
        start: dayjs(e.start).format("HH:mm:ss"),
        end: dayjs(e.end).format("HH:mm:ss"),
      }))
    );
    trigger("events");
  };

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/schedules/${scheduleId}/sequences/masters`
      );
      setPlaylists(response.data.data.playlists);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDataAndSetState = async () => {
      await fetchMasterData();
      setIsDataSet(true);
    };
    fetchDataAndSetState();
  }, []);

  useEffect(() => {
    if (isDataSet && editingSequence) {
      fetchSequence(editingSequence.id);
    }
  }, [isDataSet, editingSequence]);

  useEffect(() => {
    if (isSequenceModalOpen && events.length > 0) {
      setTimeout(() => {
        calendarRef.current?.getApi().render(); // force redraw
      }, 500); // slight delay ensures Modal is fully open
    }
  }, [isSequenceModalOpen, events]);

  const fetchSequence = async (sequenceId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/schedules/${scheduleId}/sequences/${sequenceId}`
      );
      //
      const editFormData = {
        ...response.data.data,
        events: response.data.data.events.map((event) => {
          return {
            playlistId: event.playlistId,
            start: event.start,
            end: event.end,
          };
        }),
      };

      setEvents(
        response.data.data.events.map((event, idx) => {
          return {
            id: idx + 1,
            playlist: event.playlist,
            playlistId: event.playlistId,
            date: selectedDate,
            start: dayjs(event.start, "HH:mm:ss"),
            end: dayjs(event.end, "HH:mm:ss"),
          };
        })
      );

      reset(editFormData);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = (playlistObj) => {
    const ONE_HOUR = 60 * 60; // in seconds

    const sortedEvents = [...events].sort((a, b) =>
      dayjs(a.start).isAfter(dayjs(b.start)) ? 1 : -1
    );

    const dayStart = dayjs(selectedDate).hour(9).minute(0).second(0);
    const dayEnd = dayjs(selectedDate).add(1, "day").startOf("day");

    let start, end;
    let placed = false;

    if (sortedEvents.length === 0) {
      // No events yet — place 1 hour starting at 09:00:00
      start = dayStart;
      end = start.add(1, "hour");
      placed = true;
    } else {
      // Place after the last event
      const latestEnd = sortedEvents.reduce(
        (latest, curr) =>
          dayjs(curr.end).isAfter(latest) ? dayjs(curr.end) : latest,
        dayjs(sortedEvents[0].end)
      );

      const potentialStart = latestEnd;
      const remainingSeconds = dayEnd.diff(potentialStart, "second");

      if (remainingSeconds >= ONE_HOUR) {
        // Enough space for a full hour
        start = potentialStart;
        end = start.add(1, "hour");
        placed = true;
      } else if (remainingSeconds > 0) {
        // Not enough for a full hour, use the remaining time
        start = potentialStart;
        end = dayEnd;
        placed = true;
      } else {
        // Day is full — check for gaps from 00:00:00
        const fullDayEnd = dayjs(selectedDate).add(1, "day").startOf("day");
        const earliest = dayjs(selectedDate).hour(0).minute(0).second(0);

        for (let i = 0; i <= sortedEvents.length; i++) {
          const gapStart = i === 0 ? earliest : dayjs(sortedEvents[i - 1].end);

          const gapEnd =
            i === sortedEvents.length
              ? fullDayEnd
              : dayjs(sortedEvents[i].start);

          const gapDuration = gapEnd.diff(gapStart, "second");

          if (gapDuration >= ONE_HOUR) {
            start = gapStart;
            end = start.add(1, "hour");
            placed = true;
            break;
          } else if (gapDuration > 0 && !placed) {
            // Not a full hour but better than nothing
            start = gapStart;
            end = gapEnd;
            placed = true;
          }
        }
      }
    }

    if (!placed) {
      message.error("No available slot for new event.");
      return;
    }

    const id = String(events.length);
    const newEvent = {
      id,
      playlist: playlistObj.title,
      playlistId: playlistObj.id,
      date: selectedDate,
      start,
      end,
    };

    const updatedEvents = [...events, newEvent];
    updateFormEvents(updatedEvents);

    setHighlightedEventId(id);
  };

  const handleEditEvent = (index) => {
    const event = events[index];
    setEditEventIndex(index);
    setEditStart(dayjs(event.start));
    setEditEnd(dayjs(event.end));
    setIsEditModalVisible(true);
  };

  const handleUpdateEventTime = () => {
    const newStart = dayjs(editStart);
    const newEnd = dayjs(editEnd);

    if (!newStart.isBefore(newEnd)) {
      message.error("Start time must be before end time.");
      return;
    }

    if (isOverlapping(newStart, newEnd, editEventIndex)) {
      message.error("Event overlaps with another.");
      return;
    }

    const updatedEvents = [...events];
    updatedEvents[editEventIndex].start = newStart;
    updatedEvents[editEventIndex].end = newEnd;

    updateFormEvents(updatedEvents);
    setIsEditModalVisible(false);
  };

  const handleDeleteEvent = (index) => {
    const updatedEvents = events.filter((_, idx) => idx !== index);
    updateFormEvents(updatedEvents);
  };

  const onSubmitSequence = async (data) => {
    let differFormFields = {};
    if (editingSequence !== null) {
      differFormFields = {
        id: editingSequence.id,
      };
    }
    const finalFormData = {
      name: data.name.trim(),
      events: data.events,
      ...differFormFields,
    };
    setSubmitting(true);
    let newFormData = finalFormData;
    const url =
      editingSequence === null
        ? `${process.env.REACT_APP_BASE_URL}/api/schedules/${scheduleId}/sequences/create`
        : `${process.env.REACT_APP_BASE_URL}/api/schedules/${scheduleId}/sequences/update`;

    try {
      if (editingSequence === null) {
        await axios.post(url, newFormData);
        // Reset form after successful submission
        // reset(newFormData);
        message.success("Sequence created successfully");
      } else {
        const response = await axios.put(url, newFormData);
        const updated_row_count = response.data.data.updated_row_count;
        if (updated_row_count === 1) {
          // reset(data);
          message.success("Sequence updated successfully");
        }
      }
      setSubmitting(false);
      setShowErrors(false);
      handleUpdateList();
      closeModal();
    } catch (error) {
      if (error.response && error.response.status > 300) {
        const errorData = error.response.data.message;
        // const errorField = error.response.data.field;
        console.log(errorData);
        // setShowErrors(false);
        setErrorsMessage(errorData);
        setShowErrors(true);
        setTimeout(() => {
          setShowErrors(false);
        }, 3000);

        setSubmitting(false);
      } else {
        console.error("Error:", error);
      }
    }
  };

  const filteredPlaylists = playlists.filter((p) =>
    p.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderDayView = () => {
    const calendarEvents = events.map((e, i) => ({
      id: String(i),
      title: e.playlist,
      start: e.start.toISOString(),
      end: e.end.toISOString(),
      extendedProps: { index: i },
    }));

    // Use FullCalendar’s drag/drop normally for dragging whole events:
    const handleEventDrop = (info) => {
      const idx = parseInt(info.event.id);
      const newStart = dayjs(info.event.start);
      const newEnd = dayjs(info.event.end);

      if (isOverlapping(newStart, newEnd, idx)) {
        message.error("Event overlaps with another.");
        info.revert();
        return;
      }

      const updatedEvents = [...events];
      updatedEvents[idx].start = newStart;
      updatedEvents[idx].end = newEnd;
      updateFormEvents(updatedEvents);
    };

    // Disable FullCalendar’s built-in resizing to avoid conflicts
    // We'll handle resizing manually via arrows

    const handleEventResize = (info) => {
      const idx = parseInt(info.event.id);
      const newStart = dayjs(info.event.start);
      const newEnd = dayjs(info.event.end);

      if (isOverlapping(newStart, newEnd, idx)) {
        message.error("Event overlaps with another.");
        info.revert();
        return;
      }

      const updatedEvents = [...events];
      updatedEvents[idx].start = newStart;
      updatedEvents[idx].end = newEnd;
      updateFormEvents(updatedEvents);
    };

    // Custom event content:
    const renderEventContent = (arg) => {
      const index = arg.event.extendedProps.index;
      const start = dayjs(arg.event.start).format("HH:mm");
      const end = dayjs(arg.event.end).format("HH:mm");

      return (
        <div
          className="fc-custom-event"
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            padding: "0 2px",
            fontSize: 12,
            userSelect: "none",
          }}
        >
          {/* Drag handle */}
          <div
            className="fc-event-drag-handle"
            style={{
              cursor: "move",
              marginRight: 8,
              fontSize: 16,
              lineHeight: 1,
            }}
            title="Drag to move"
          >
            <DragOutlined />
          </div>

          {/* Event info */}
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{ fontWeight: "bold" }}>
              {start} - {end}
            </div>
            <div
              style={{
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              {arg.event.title}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ marginLeft: 8, whiteSpace: "nowrap" }}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditEvent(index)}
              style={{ marginRight: 4 }}
            />
            <Button
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDeleteEvent(index)}
            />
          </div>
        </div>
      );
    };

    return (
      <>
        <style>{`
          .fc-col-header, .fc-daygrid-day-top {
            display: none !important;
          }
          .fc-timegrid-slot {
            height: 1.5em !important;
          }
        `}</style>

        <FullCalendar
          key={editingSequence?.id || "new"}
          ref={calendarRef}
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView="timeGridDay"
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          slotDuration="00:10:00"
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          editable={true}
          height={450}
          events={calendarEvents}
          eventDrop={handleEventDrop}
          eventContent={renderEventContent}
          initialDate={selectedDate.toISOString()}
          headerToolbar={false}
          allDaySlot={false}
          dayHeaderContent={() => null}
          eventStartEditable={true} // Allow drag to move event
          eventDurationEditable={true} // Enable default resizing
          eventResize={handleEventResize}
          eventDidMount={(info) => {
            if (info.event.id === highlightedEventId) {
              info.el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }}
        />
      </>
    );
  };

  return (
    <>
      <Modal
        open={isSequenceModalOpen}
        onCancel={closeModal}
        footer={null}
        style={{ inset: "15px 0" }}
        title={editingSequence ? "Edit Sequence" : "Add New Sequence"}
        width={"90%"}
        maskClosable={false}
      >
        {loading ? (
          <ButtonLoader wrapperClass={`min-h-50`} />
        ) : (
          <>
            {showErrors && (
              <>
                <div className="col-sm-12 mb-3">
                  <Alert
                    message={errorsMessage}
                    type="error"
                    closeIcon={false}
                  />
                </div>
              </>
            )}
            <form onSubmit={handleSubmit(onSubmitSequence)}>
              <div className="mb-3">
                <label className="form-label">
                  Sequence Name <span className="text-danger">*</span>
                </label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Sequence Name"
                      disabled={!!editingSequence}
                      maxLength={20}
                    />
                  )}
                />
                {errors.name && (
                  <div className="text-danger small">{errors.name.message}</div>
                )}
              </div>

              <Row gutter={24}>
                <Col span={8}>
                  <Input
                    placeholder="Search playlists"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {errors.events && (
                    <div className="text-danger small">
                      {errors.events.message}
                    </div>
                  )}
                  <List
                    className="mt-3"
                    bordered
                    dataSource={filteredPlaylists}
                    renderItem={(item) => (
                      <List.Item
                        onClick={() => handleAddEvent(item)}
                        style={{ cursor: "pointer" }}
                      >
                        {item.title}
                      </List.Item>
                    )}
                  />
                </Col>
                <Col span={16} style={{ height: "450px" }}>
                  {events.length === 0 ? (
                    <div
                      style={{
                        padding: 20,
                        textAlign: "center",
                        color: "#888",
                      }}
                    >
                      Click on any playlist to create a day sequence.
                    </div>
                  ) : (
                    renderDayView()
                  )}
                </Col>
              </Row>
              <Row justify="end" style={{ marginTop: 16 }}>
                <Button type="primary" htmlType="submit" disabled={submitting}>
                  {!submitting ? (
                    editingSequence ? (
                      "Update Sequence"
                    ) : (
                      "Add Sequence"
                    )
                  ) : (
                    <ButtonLoader text="Submitting..." />
                  )}
                </Button>
              </Row>
            </form>
          </>
        )}
      </Modal>
      <Modal
        title={`Edit Timings`}
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={handleUpdateEventTime}
        okText="Update"
      >
        <div style={{ marginBottom: 12 }}>
          <label>Start Time</label>
          <TimePicker
            value={editStart}
            onChange={(value) => setEditStart(value)}
            format="HH:mm"
            minuteStep={10}
            style={{ width: "100%" }}
            showNow={false}
          />
        </div>
        <div>
          <label>End Time</label>
          <TimePicker
            value={editEnd}
            onChange={(value) => setEditEnd(value)}
            format="HH:mm"
            minuteStep={10}
            style={{ width: "100%" }}
            showNow={false}
          />
        </div>
      </Modal>
    </>
  );
};

export default SequenceForm;
